import { useApolloClient } from '@apollo/client';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import writeXlsxFile, { Row } from 'write-excel-file';

import * as graphql from '@treams/treams-network';
import { Paragraph } from '@treams/treams-ui';

import BulkModal from './BulkModal';

// The workspace roles whose labels don't follow the `roles.<id>` convention,
// mirroring the labels of the role picker in the employee's settings.
const WORKSPACE_ROLE_LABELS: Record<string, string> = {
	'workspace-administrator': 'roles.administrator',
	'workspace-hr-business-partner': 'roles.HRBusinessPartner',
};

export default function BulkExportEmployeesModal(props: {
	users: graphql.User[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();

	const client = useApolloClient();

	// The selection comes from the search index, which holds neither the HRIS
	// reference, nor the workspace role, nor the department name.
	const fetchEmployees = async (users: graphql.User[]) => {
		const employees: graphql.EmployeeListExportQuery['usersByIDs'] = [];

		for (const chunk of _.chunk(
			users.map((user) => user.id),
			100,
		)) {
			const response = await client.query<graphql.EmployeeListExportQuery>({
				query: graphql.EmployeeListExportDocument,
				variables: { ids: chunk },
				fetchPolicy: 'network-only',
			});

			employees.push(...response.data.usersByIDs);
		}

		return employees;
	};

	const exportEmployeesToXLSX = async (users: graphql.User[]) => {
		const employees = await fetchEmployees(users);

		const headers: Row = [
			{ value: t('employees.table.header.firstName') },
			{ value: t('employees.table.header.lastName') },
			{ value: t('employees.table.header.email') },
			{ value: t('employees.table.header.username') },
			{ value: t('employees.table.header.lastLoginAt') },
			{ value: t('settings.employees.personal.hris') },
			{ value: t('settings.employees.authentication.role') },
			{ value: t('settings.employees.personal.department') },
		];

		const rows: Row[] = employees.map((employee) => {
			const lastLoginAt = employee.lastLoginAt
				? moment(employee.lastLoginAt).format('DD-MM-YYYY HH:mm')
				: '-';

			const roleID = employee.workspaceRole?.role.id;

			return [
				{ value: employee.firstName },
				{ value: employee.lastName },
				{ value: employee.email },
				{ value: employee.username },
				{ value: lastLoginAt },
				{ value: employee.externalReferenceID ?? '-' },
				{ value: roleID ? t(WORKSPACE_ROLE_LABELS[roleID] ?? `roles.${roleID}`) : '-' },
				{ value: employee.group.name },
			];
		});

		const data = [headers, ...rows];
		const now = moment().format('DD-MM-YYYY');
		const fileName = `${t('settings.employees.title')} - ${now}.xlsx`;

		await writeXlsxFile(data, {
			fileName,
			columns: [
				{ width: 25 },
				{ width: 25 },
				{ width: 35 },
				{ width: 20 },
				{ width: 15 },
				{ width: 25 },
				{ width: 25 },
				{ width: 25 },
			],
		});
	};

	return (
		<BulkModal
			name="bulkExportEmployees"
			valid
			apply={async (user) => {}}
			applySingle={exportEmployeesToXLSX}
			users={props.users}
			onDismiss={props.onDismiss}
		>
			<Paragraph mb={4}>{t('employees.bulkExportEmployees.instructions')}</Paragraph>
		</BulkModal>
	);
}
